import { ThumbsDown, ThumbsUp } from "lucide-react";
import { ReactNode } from "react";
import { useImmer } from "use-immer";

import {
  IgDialog,
  IgIconButton,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { AiFeature, SubmitAiFeedbackBody } from "@intelligems/ig-types";
import { Stack, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useSubmitAiFeedback } from "@src/api/ai/feedback/submit-feedback";
import { mpRateAi } from "@src/utils/mixpanel";

export type FeedbackTarget =
  | "mixpanel" // Only send to mixpanel
  | "ai"; // Send to mixpanel + ai.intelligems.io (web/ai)

interface RateAiProps {
  prependExtraButtons?: ReactNode;
  sessionId: string;
  feature: AiFeature;
  text?: ReactNode;
  metadata?: Record<string, string>;
  showCommentsDialog?: boolean;
  /** Initial rating value if feedback was already submitted */
  initialRating?: "good" | "bad" | null;
  /** Where to send feedback. Defaults to "mixpanel" */
  feedbackTarget?: FeedbackTarget;
  /** Required when feedbackTarget is "ai" */
  workflowId?: string;
  /** Optional additional payload fields for the "ai" target */
  aiFeedbackPayload?: Partial<
    Omit<SubmitAiFeedbackBody, "sessionId" | "workflowId" | "rating">
  >;
}

type Rating = "good" | "bad";

export const RateAi = ({
  prependExtraButtons,
  sessionId,
  feature,
  text,
  showCommentsDialog = false,
  metadata = {},
  initialRating = null,
  feedbackTarget = "mixpanel",
  workflowId,
  aiFeedbackPayload,
}: RateAiProps) => {
  const theme = useTheme();
  const [rated, setRated] = useImmer<Rating | null>(initialRating);
  const [dialogOpen, setDialogOpen] = useImmer(false);
  const [pendingRating, setPendingRating] = useImmer<Rating | null>(null);
  const [comments, setComments] = useImmer("");

  const aiFeedback = useSubmitAiFeedback();

  const handleRatingSubmit = (
    rating: Rating,
    additionalComments?: string,
    metadata?: Record<string, string>
  ) => {
    if (feedbackTarget !== "ai") {
      mpRateAi(feature, rating, sessionId, additionalComments, metadata);
    }

    if (feedbackTarget === "ai" && workflowId) {
      aiFeedback.mutate({
        sessionId,
        workflowId,
        rating,
        comment: additionalComments,
        ...aiFeedbackPayload,
      });
    }

    setRated(rating);
  };

  const onClick = (rating: Rating) => {
    if (showCommentsDialog) {
      setPendingRating(rating);
      setDialogOpen(true);
    } else {
      handleRatingSubmit(rating);
    }
  };

  const handleDialogSubmit = () => {
    if (pendingRating) {
      handleRatingSubmit(pendingRating, comments, metadata);
      setDialogOpen(false);
      setComments("");
      setPendingRating(null);
    }
  };

  const handleDialogClose = () => {
    // Submit rating even without comments
    if (pendingRating) {
      handleRatingSubmit(pendingRating, undefined, metadata);
    }
    setDialogOpen(false);
    setComments("");
    setPendingRating(null);
  };

  if (!sessionId) {
    return text ? <Grid>{text}</Grid> : null;
  }

  const buttons = (
    <Stack direction={"row"} spacing={0.5}>
      {prependExtraButtons}
      <IgIconButton
        size={"small"}
        onClick={() => onClick("good")}
        disabled={!!rated}
      >
        <ThumbsUp
          size={14}
          color={rated === "good" ? theme.igIconColors.success : undefined}
        />
      </IgIconButton>
      <IgIconButton
        size={"small"}
        onClick={() => onClick("bad")}
        disabled={!!rated}
      >
        <ThumbsDown
          size={14}
          color={rated === "bad" ? theme.igIconColors.error : undefined}
        />
      </IgIconButton>
    </Stack>
  );

  if (!text) {
    return (
      <>
        {buttons}
        {showCommentsDialog && (
          <IgDialog
            showDialog={dialogOpen}
            title="Thank you for your feedback!"
            handleClose={handleDialogClose}
            handleYesClick={handleDialogSubmit}
            handleNoClick={handleDialogClose}
            yesButtonText="Submit"
            noButtonText="Skip"
            dialogContentWidth={"md"}
            buttonVariant="contained"
            content={
              <Grid container direction={"column"} spacing={2}>
                <Grid>
                  <IgTypography>
                    You rated this response as{" "}
                    <IgTypography fontWeight={600}>
                      {pendingRating === "good" ? "helpful" : "not helpful"}
                    </IgTypography>
                    .
                  </IgTypography>
                </Grid>
                <Grid>
                  <IgTextField
                    label="Additional Comments (optional)"
                    multiline
                    rows={4}
                    fullWidth
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder="Tell us more about your experience..."
                  />
                </Grid>
              </Grid>
            }
          />
        )}
      </>
    );
  }

  return (
    <>
      <Grid>
        {text}
        {buttons}
      </Grid>
      {showCommentsDialog && (
        <IgDialog
          showDialog={dialogOpen}
          title="Thank you for your feedback!"
          handleClose={handleDialogClose}
          handleYesClick={handleDialogSubmit}
          handleNoClick={handleDialogClose}
          yesButtonText="Submit"
          noButtonText="Skip"
          dialogContentWidth={"md"}
          buttonVariant="contained"
          content={
            <Grid container direction={"column"} spacing={2}>
              <Grid>
                <IgTypography>
                  You rated this response as{" "}
                  <IgTypography fontWeight={600}>
                    {pendingRating === "good" ? "helpful" : "not helpful"}
                  </IgTypography>
                  .
                </IgTypography>
              </Grid>
              <Grid>
                <IgTextField
                  label="Additional Comments (optional)"
                  multiline
                  rows={4}
                  fullWidth
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Tell us more about your experience..."
                />
              </Grid>
            </Grid>
          }
        />
      )}
    </>
  );
};
